// The two order hand-offs: card -> pop-up, and nothing in between.
//
// D5 / guarantee G11: the adjustment and the hedges stay TWO transactions. Nothing here offers to
// send them as one, and there is no third function.
//
// THE RULE THIS FILE EXISTS TO KEEP: Maven maps nothing. Both arrays are built on the backend in the
// shapes the pop-ups consume, and they are handed over exactly as delivered — no map, no filter, no
// sort, no re-shape, no re-price. The pop-ups write the after-state slots themselves; this lane
// writes no slot.
//
// If you are about to add a `.map(...)` between a card and a `show(...)` call, the mapping belongs
// on the backend and the contract is wrong. Raise it; do not fix it here.
//
// Plain functions, not a service: there is no state to hold, and a caller passes the pop-up it owns
// through its own @ViewChild. That keeps the whole hand-off testable without a template.

import {ApgPortfolio} from '../../adjustment-pricing-grid/model/ApgPortfolio';
import {AdjustmentSolutionPopupComponent} from '../../adjustment-pricing-grid/solution-popup/adjustment-solution-popup.component';
import {HgTransactionPopupComponent} from '../../hedging-grid/transaction-popup/hg-transaction-popup.component';
import {MavenCard, MavenResultReady} from '../results/maven-delivery.contract';

const STRATEGY_TYPE_SINGLE = 'single';
const STRATEGY_TYPE_DOUBLE = 'double';

/**
 * The run-level facts both pop-ups need. Delivered on MavenResultReadyDto, never worked out here.
 */
export interface MavenOrderContext {
    portfolio: ApgPortfolio;
    defaultQty: number;
    strategyType: string;               // "single" | "double" (ruling L39)
}

/**
 * Read the run's strategy type off the delivery.
 *
 * DELIVERED, NOT INFERRED (ruling L39). Both ways of getting this wrong are silent: a wrong
 * `isDoubleMode` makes the hedge pop-up filter legs out of the transaction inside itself, after this
 * lane has passed everything correctly; a wrong `strategyType` writes the after-state into the wrong
 * slot. Neither raises anything, so neither can be guessed from the legs on a card — the backend
 * read the ordered template and knows the answer.
 *
 * A value outside the closed pair is a defect, not something to accommodate (guarantee G12): this
 * returns null and the caller must refuse to open the pop-up rather than pick a default.
 */
export function resolveStrategyType(delivery: MavenResultReady): 'single' | 'double' | null {

    const value = delivery?.strategyType;

    if (value === STRATEGY_TYPE_SINGLE) {
        return STRATEGY_TYPE_SINGLE;
    }

    if (value === STRATEGY_TYPE_DOUBLE) {
        return STRATEGY_TYPE_DOUBLE;
    }

    return null;
}

/**
 * Open the adjustment order for a card.
 *
 * Returns false when it did not open, so the caller does not light an applied mark for an order the
 * trader never saw.
 */
export function openMavenAdjustmentOrder(
    popup: AdjustmentSolutionPopupComponent,
    context: MavenOrderContext,
    card: MavenCard,
    cardKey: string
): boolean {

    if (!popup || !context?.portfolio || !card) {
        return false;
    }

    const adjustments = card.adjustment?.adjustments;

    if (!adjustments || adjustments.length === 0) {
        return false;
    }

    const strategyType = context.strategyType === STRATEGY_TYPE_DOUBLE
        ? STRATEGY_TYPE_DOUBLE
        : context.strategyType === STRATEGY_TYPE_SINGLE ? STRATEGY_TYPE_SINGLE : null;

    if (strategyType === null) {
        return false;
    }

    popup.show(
        context.portfolio,

        // EXACTLY as delivered. No copy, no sort, no filter — the pop-up's input is the backend's
        // output, and this lane is a wire between them.
        adjustments,

        strategyType,

        // `source` is a CLOSED, REQUIRED union — 'PriceBox' | 'Zones Grid' — with no Maven member
        // (ruling L41). Maven passes 'PriceBox' because the argument is INERT: in
        // adjustment-solution-popup.component.ts it is declared (:123), taken (:203) and assigned
        // (:215), and read NOWHERE — not in the component, not in its template, not by any other
        // file. Verified at 7135fc4a. Widening a closed union in a file this lane does not own, to
        // carry a label nobody reads, is churn.
        //
        // THE CONDITION ON THAT: if `source` ever becomes behavioural, Maven needs its OWN member
        // and must not keep masquerading as the Price Box. This comment is what makes an arbitrary
        // value safe; without it the call is simply a lie about where the order came from.
        'PriceBox',

        // THE CARD'S IDENTITY, echoed back on the pop-up's save — the only signal that says WHO
        // saved. Without it, "a save happened somewhere" plus "the card Maven last opened" lights a
        // Maven mark when the trader saves in the APG, and a false mark is what the ruling forbids.
        cardKey
    );

    return true;
}

/**
 * Open the hedge order for a card: the close of the old hedges (when the trader chose Close) and the
 * opening of the new ones, in ONE dialog.
 *
 * One button, not separate Calls and Puts (D4) — the dialog contains the regroup, which may build
 * iron structures across the two sides, so splitting them here would break what it can produce.
 */
export function openMavenHedgesOrder(
    popup: HgTransactionPopupComponent,
    context: MavenOrderContext,
    card: MavenCard,
    cardKey: string
): boolean {

    if (!popup || !context?.portfolio || !card) {
        return false;
    }

    const transactions = card.hedges?.transaction;

    if (!transactions || transactions.length === 0) {
        return false;
    }

    if (context.strategyType !== STRATEGY_TYPE_SINGLE && context.strategyType !== STRATEGY_TYPE_DOUBLE) {
        return false;
    }

    // The flag the pop-up filters on. `hg-transaction-popup.component.ts:183-185` runs
    // `transactions.filter(x => !isVoid(x.orderTransactions))` when this is false — so legs handed
    // over correctly are dropped INSIDE the pop-up if this is wrong, with nothing raised anywhere.
    // It comes from the delivery for that reason (ruling L39).
    const isDoubleMode = context.strategyType === STRATEGY_TYPE_DOUBLE;

    popup.show(
        context.portfolio,
        context.defaultQty,

        // EXACTLY as delivered — close plus open, built on the backend.
        transactions,

        isDoubleMode,

        // THE CARD'S IDENTITY, echoed back on the pop-up's save (owner ruling, 2026-09-11). This is
        // the parameter Package Comparison already uses for the same purpose: the caller supplies an
        // id, the pop-up publishes it on 'Hg.SaveAfterState', and the caller matches it to one of its
        // own cards. Without it a save is anonymous and the mark would have to be guessed.
        cardKey
    );

    return true;
}
